import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, Calendar, CreditCard, Minus, Plus } from "lucide-react";
import { useSiteContent } from "../admin/SiteContentContext";
import Modal from "../components/Modal";
import PaymentOptions from "../components/PaymentOptions";

export default function Services() {
  const { content } = useSiteContent();
  const { servicesList, paymentSettings } = content;
  const [payFor, setPayFor] = React.useState(null); // service index being paid for
  const [hours, setHours] = React.useState(2);
  const [buyerName, setBuyerName] = React.useState("");
  const [buyerPhone, setBuyerPhone] = React.useState("");
  const navigate = useNavigate();

  const open = payFor !== null ? servicesList[payFor] : null;
  const amount = open ? Number((open.price || 0) * (Number(hours) || 1)) : 0;

  return (
    <main>
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <span className="kicker">What we do</span>
          <h1 className="hero-heading text-white text-5xl md:text-6xl mt-5">Support for the life happening at home.</h1>
          <p className="text-slate-300 text-lg max-w-3xl mx-auto mt-6 leading-relaxed">
            Our non-medical services are designed around comfort, connection, and the practical details that make a day feel more manageable.
          </p>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {servicesList.map((s, i) => (
            <div key={s.num} className="card-soft p-7 hover:border-[#c7e34a]/30 transition-colors group flex flex-col">
              <div className="flex items-start justify-between">
                <div className="text-[#c7e34a] font-serif-display text-3xl">{s.num}</div>
                {s.price > 0 && (
                  <div className="text-right">
                    <div className="text-white font-serif-display text-2xl leading-none">${s.price}</div>
                    <div className="text-[11px] text-slate-500 uppercase tracking-widest">per {s.unit || "hr"}</div>
                  </div>
                )}
              </div>
              <h3 className="font-serif-display text-white text-2xl mb-3 mt-4 group-hover:text-[#c7e34a] transition">{s.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed flex-1">{s.desc}</p>
              <div className="mt-6 flex items-center gap-2">
                <button onClick={() => { setPayFor(i); setHours(2); }}
                  className="btn-lime text-sm flex-1 justify-center">
                  <CreditCard className="w-4 h-4" /> Book & pay
                </button>
                <Link to="/booking" className="btn-ghost text-sm !px-3" aria-label="schedule only">
                  <Calendar className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="card-soft p-10 md:p-14 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#c7e34a]/10 blur-3xl" />
            <div className="relative">
              <span className="kicker"><ShieldCheck className="w-3.5 h-3.5" /> Important to know</span>
              <h2 className="font-serif-display text-white text-4xl md:text-5xl mt-4 mb-5">We are a non-medical care partner.</h2>
              <p className="text-slate-300 leading-relaxed max-w-2xl mb-8">3E DanCare does not provide skilled nursing, medical services, medication administration, or clinical care. We work alongside the professionals already supporting your health.</p>
              <Link to="/booking" className="btn-lime">Talk with our team <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      <Modal open={payFor !== null} onClose={() => setPayFor(null)}
        title={open ? `${open.title} — Book & pay` : ""} maxWidth="max-w-xl">
        {open && (
          <div className="space-y-5">
            <div className="bg-[#0b1220] border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-slate-200 font-medium">{open.title}</div>
                  <div className="text-xs text-slate-400">${open.price} per {open.unit || "hr"}</div>
                </div>
                <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full p-1">
                  <button type="button" onClick={() => setHours(h => Math.max(1, h - 1))}
                    className="w-8 h-8 rounded-full bg-white/5 text-slate-200 hover:bg-white/10 flex items-center justify-center">
                    <Minus className="w-4 h-4" />
                  </button>
                  <div className="min-w-[60px] text-center text-white">
                    <span className="font-semibold">{hours}</span> <span className="text-slate-400 text-xs">{open.unit || "hr"}{hours > 1 ? "s" : ""}</span>
                  </div>
                  <button type="button" onClick={() => setHours(h => Math.min(12, h + 1))}
                    className="w-8 h-8 rounded-full bg-white/5 text-slate-200 hover:bg-white/10 flex items-center justify-center">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Your name *</label>
                <input value={buyerName} onChange={e => setBuyerName(e.target.value)}
                  className="w-full bg-[#0b1220] border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#c7e34a]/60"
                  placeholder="Full name" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Phone *</label>
                <input value={buyerPhone} onChange={e => setBuyerPhone(e.target.value)}
                  className="w-full bg-[#0b1220] border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#c7e34a]/60"
                  placeholder="(xxx) xxx-xxxx" />
              </div>
            </div>

            <PaymentOptions amount={amount}
              purpose={`${open.title} · ${hours} ${open.unit || "hr"}${hours > 1 ? "s" : ""}`}
              meta={{ serviceTitle: open.title, hours, rate: open.price, name: buyerName, phone: buyerPhone }}
              onConfirmed={() => {
                setTimeout(() => { setPayFor(null); navigate("/booking"); }, 1800);
              }} />

            <p className="text-xs text-slate-500 text-center">After payment, we'll call to confirm date & time. You can also <Link to="/booking" className="text-[#c7e34a] underline">pick a slot now</Link>.</p>
          </div>
        )}
      </Modal>
    </main>
  );
}
